/**
 * Символ объекта каталога на чертеже — компонент по id элемента.
 *
 * Каталог хранит только данные объекта; как он выглядит, знает его раздел:
 * реестры символов лежат рядом с компонентами раздела (`electrical/2d/symbols` и т. д.),
 * здесь они только собраны в одну таблицу. Элемент без символа (тип удалён
 * из каталога или символ ещё не нарисован) рисуется рамкой-заглушкой.
 *
 * Для развёртки у предмета свои рисунки — спереди и сбоку (`elevationSymbols` раздела).
 * Если вид для развёртки нарисован, он и рисуется; нет — символ сам решает, как выглядеть
 * с этой стороны (настенный телевизор, кондиционер и вытяжка умеют это внутри себя).
 */
import {
	catalogSectionKeyOfItem,
	type CatalogItem,
	type CatalogSectionKey,
} from "@/shared/catalog";
import { CatalogSectionKind } from "@/shared/plan";
import type { ElevationSymbols, SymbolComponent, SymbolFacing } from "@/shared/ui";
import { CustomFurnitureSymbol, hasCustomFrontView } from "./custom/2d/CustomFurnitureSymbol";
import { DECOR_ELEVATION_SYMBOLS } from "./decor/2d/elevationSymbols";
import { DECOR_SYMBOLS } from "./decor/2d/symbols";
import { ELECTRICAL_SYMBOLS } from "./electrical/2d/symbols";
import { FURNITURE_ELEVATION_SYMBOLS } from "./furniture/2d/elevationSymbols";
import { FURNITURE_SYMBOL_BY_ID } from "./furniture/2d/symbols";
import { LIGHTING_SYMBOLS } from "./lighting/2d/symbols";
import { PLUMBING_ELEVATION_SYMBOLS } from "./plumbing/2d/elevationSymbols";
import { PLUMBING_SYMBOLS } from "./plumbing/2d/symbols";
import { WATER_SYMBOLS } from "./water/2d/symbols";

const SYMBOLS: Partial<Record<CatalogSectionKey, Record<string, SymbolComponent>>> = {
	[CatalogSectionKind.Electrical]: ELECTRICAL_SYMBOLS,
	[CatalogSectionKind.Lighting]: LIGHTING_SYMBOLS,
	[CatalogSectionKind.Furniture]: Object.fromEntries(FURNITURE_SYMBOL_BY_ID),
	[CatalogSectionKind.Decor]: DECOR_SYMBOLS,
	[CatalogSectionKind.Plumbing]: PLUMBING_SYMBOLS,
	[CatalogSectionKind.Water]: WATER_SYMBOLS,
};

const ELEVATION_SYMBOLS: Partial<Record<CatalogSectionKey, Record<string, ElevationSymbols>>> = {
	[CatalogSectionKind.Furniture]: FURNITURE_ELEVATION_SYMBOLS,
	[CatalogSectionKind.Decor]: DECOR_ELEVATION_SYMBOLS,
	[CatalogSectionKind.Plumbing]: PLUMBING_ELEVATION_SYMBOLS,
};

/**
 * Нарисован ли предмет для развёртки с этой стороны. Спереди нарисован, а сбоку нет —
 * у предмета нет своего бокового вида: сбоку он просто коробка.
 */
export const hasElevationSymbol = (
	item: CatalogItem | null,
	facing: Exclude<SymbolFacing, "plan">,
): boolean => {
	const section = item ? catalogSectionKeyOfItem(item) : null;
	// Шкаф и модуль из конструктора рисуются спереди по своим деталям
	if (item && section === CatalogSectionKind.CustomFurniture) {
		return facing === "front" && hasCustomFrontView(item.id);
	}
	return Boolean(item && section && ELEVATION_SYMBOLS[section]?.[item.id]?.[facing]);
};

interface Props {
	/** Элемент каталога; null — тип удалён из каталога */
	item: CatalogItem | null;
	/** Габариты на чертеже, мм: символ вписывается в этот прямоугольник вокруг начала координат */
	width: number;
	height: number;
	color: string;
	/** Размер миллиметра в пикселях — для толщины рамки-заглушки */
	mmPerPx: number;
	/** Сверху (план) или спереди (развёртка) — символ может отличаться */
	facing?: SymbolFacing;
}

export const CatalogSymbol = ({ item, width, height, color, mmPerPx, facing = "plan" }: Props) => {
	const section = item ? catalogSectionKeyOfItem(item) : null;
	// Мебель из конструктора рисуется по своему проекту, а не по реестру id
	if (item && section === CatalogSectionKind.CustomFurniture) {
		return (
			<CustomFurnitureSymbol
				itemId={item.id}
				width={width}
				height={height}
				color={color}
				facing={facing}
			/>
		);
	}
	const Glyph =
		(item && section && facing !== "plan"
			? ELEVATION_SYMBOLS[section]?.[item.id]?.[facing]
			: undefined) ?? (item && section ? SYMBOLS[section]?.[item.id] : undefined);
	if (!Glyph) {
		return (
			<rect
				x={-width / 2}
				y={-height / 2}
				width={width}
				height={height}
				fill="none"
				stroke={color}
				strokeWidth={1.2 * mmPerPx}
				strokeDasharray={`${3 * mmPerPx} ${2 * mmPerPx}`}
			/>
		);
	}
	return <Glyph width={width} height={height} color={color} facing={facing} />;
};

/**
 * Размер символа с учётом нижней границы на экране.
 *
 * Розетка — это 80 мм на стене в несколько метров: при отдалении она
 * превращается в точку, по которой не попасть мышью. Поэтому символ не даём
 * уменьшаться ниже `minPx` экранных пикселей. Порог считается по большей
 * стороне: у полотенцесушителя на плане глубина всего 100 мм при ширине 500,
 * и если мерить по глубине, при отдалении лесенка раздувается на метры.
 * Формула нужна и самому символу, и тем, кто его размещает, — поэтому вынесена отдельно.
 */
export const symbolRenderSize = (
	width: number,
	height: number,
	mmPerPx: number,
	scale = 1,
	minPx = 0,
): { width: number; height: number; zoom: number } => {
	const zoom = Math.max(scale, (minPx * mmPerPx) / Math.max(1, width, height));
	return { width: width * zoom, height: height * zoom, zoom };
};
